import React, { useCallback, useEffect, useMemo, useState,useRef } from "react";
import { Box, Button, FormControl, FormControlLabel, FormHelperText, Grid, Radio, RadioGroup, Typography, Tabs, Tab, useTheme, useMediaQuery } from "@mui/material";
import { Formik, Field } from "formik";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import arrowRightVerify from "../../assets/svgIcons/arrow-right-verify.svg";
import ModalPopup from "./ModalPopup";
import { PrimaryButton } from "./Button";
import SelectOptions from "./SelectOptions";
import { InputField } from "./Inputs";
import BasicDatePicker from "./BasicDatePicker";
import AutoCompleteOptions from "./AutoComplete";
import losServices from "../../services/api/service/losService";
import dropdownService from "../../services/api/service/dropdownService";
import applicantDetailsServices from "../../services/api/service/applicantDetailsService";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { empTypeOptions, handleOwnershipStatusValue, mapGender,resolveBirthCityId} from "../../utils/helpers";
import { buildCoApplicantSaveIdDto } from "./StepperDyanmicForm/helperDto";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../services/redux/store";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { setEncryptionKey } from "../../services/redux/slices/stepperDynamicFormSlice";
import { encryptData } from "../../utils/crypto";
import { getSanitizedPublicKeyFromResponse } from "../../utils/piiKey";
import dayjs from "dayjs";
import {
  Option,
  PAN_REGEX,
  buildEmpSaveFormData,
  buildSaveFormData,
  buildStoreUserFieldsPayload,
  createFieldIdResolver,
  getAutocompleteOptionLabel,
  getAutocompleteOptionValue,
  getCoApplicantEmpFieldNames,
  getCoApplicantAddressFieldNames,
  getCoApplicantFieldNames,
  getValidationSchema,
  normalizeValues,
  buildAddressSaveFormData,
} from "./AddNewCoApplicantModal.module";
import AddressDetailsTab from "./coAppTabs/addressDetails";
import ConfirmationModal from "./coAppTabs/confirmationModal";
import EmployementDetailsTab from "./coAppTabs/employementDetails";

export interface AddNewCoApplicantModalProps {
  isOpen: boolean;
  onClose: () => void;
  disabled?: boolean;
  nextCoApplicantNumber: number;
  trackingId: string;
  applicationId?: string | number | null;
  onSuccess?: () => Promise<void> | void;
  preloadedRelationshipOptions?: Option[];
  preloadedSalutationOptions?: Option[];
  preloadedNationalityOptions?: Option[];
  preloadedMaritalStatusOptions?: Option[];
  preloadedCountryOptions?: Option[];
}

const AddNewCoApplicantModal: React.FC<AddNewCoApplicantModalProps> = ({
  isOpen,
  onClose,
  disabled = false,
  nextCoApplicantNumber,
  trackingId,
  applicationId,
  onSuccess,
  preloadedRelationshipOptions = [],
  preloadedSalutationOptions = [],
  preloadedNationalityOptions = [],
  preloadedMaritalStatusOptions = [],
  preloadedCountryOptions = [],
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const encryptionKey = useSelector(
    (state: RootState) => state.stepper.encryptionKey
  );

  const fetchSanitizedPiiPublicKey = useCallback(async () => {
    if (encryptionKey) {
      return getSanitizedPublicKeyFromResponse(encryptionKey);
    }
    const piiKeyResponse: any = await losServices.getPiiKey();
    if (piiKeyResponse?.status !== 200) return "";

    const rawKey = piiKeyResponse?.data?.p_k;
    const rawKeyStr = typeof rawKey === "string" ? rawKey : "";
    dispatch(setEncryptionKey(rawKeyStr));

    return getSanitizedPublicKeyFromResponse(rawKey);
  }, [encryptionKey, dispatch]);

  const [relationshipOptions, setRelationshipOptions] = useState<Option[]>([]);
  const [salutationOptions, setSalutationOptions] = useState<Option[]>([]);
  const [nationalityOptions, setNationalityOptions] = useState<Option[]>([]);
  const [maritalStatusOptions, setMaritalStatusOptions] = useState<Option[]>([]);
  const [countryOptions, setCountryOptions] = useState<Option[]>([]);
  const [cityOfBirthOptions, setCityOfBirthOptions] = useState<Option[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingBirthCities, setIsLoadingBirthCities] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Surfaced up from AddressDetailsTab/EmployementDetailsTab so Save stays
  // disabled while their pincode lookups (getZipCode + getPinCode) are in
  // flight, instead of only showing a spinner on the pincode field itself.
  const [addressZipCodeLoading, setAddressZipCodeLoading] = useState(false);
  const [employmentZipCodeLoading, setEmploymentZipCodeLoading] = useState(false);
  const isPincodeLookupInProgress = addressZipCodeLoading || employmentZipCodeLoading;
  const [isPanVerifying, setIsPanVerifying] = useState(false);
  const [verifiedPanSignature, setVerifiedPanSignature] = useState("");
  const lastBirthCityCountryRef = useRef("");
  const birthCityRequestIdRef = useRef(0);
  const [empTypeError, setEmpTypeError] = useState<string>("");
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [permanentCodeData, setPermanentCodeData] = useState<any>(null);
  const [currentCodeData, setCurrentCodeData] = useState<any>(null);
  const [companyCodeData, setCompanyCodeData] = useState<any>(null);
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const fieldNames = useMemo(
    () => getCoApplicantFieldNames(nextCoApplicantNumber),
    [nextCoApplicantNumber]
  );
  const {
    relationshipField,
    relationValueField,
    salutationField,
    firstNameField,
    lastNameField,
    genderField,
    dobField,
    panField,
    phoneField,
    emailField,
    nationalityField,
    maritalStatusField,
    countryOfBirthField,
    cityOfBirthField,
    payPartyField,
  } = fieldNames;
   const employmentFieldNames = useMemo(
    () => getCoApplicantEmpFieldNames(nextCoApplicantNumber),
    [nextCoApplicantNumber]
  );

  const {
    isEmployedField,
    employmentTypeField,
  } = employmentFieldNames;
  const resolveAutocompleteOption = useCallback(
    (value: any, options: Option[]) => {
      const selectedValue = getAutocompleteOptionValue(value).toLowerCase();
      const selectedLabel = getAutocompleteOptionLabel(value).toLowerCase();

      return (
        options.find((opt) => {
          const optionValue = String(opt.value ?? "").trim().toLowerCase();
          const optionLabel = String(opt.label ?? "").trim().toLowerCase();

          return (
            (selectedValue && optionValue === selectedValue) ||
            (selectedValue && optionLabel === selectedValue) ||
            (selectedLabel && optionLabel === selectedLabel) ||
            (selectedLabel && optionValue === selectedLabel)
          );
        }) || null
      );
    },
    []
  );

  const addressFieldNames = useMemo(
    () => getCoApplicantAddressFieldNames(nextCoApplicantNumber),
    [nextCoApplicantNumber]
  );


  const genderOptions = useMemo(() => {
    const genders = mapGender() as Array<{ code: string; name: string }>;
    return genders.map((g) => ({
      value: g.name.toLowerCase(),
      label: g.name,
    }));
  }, []);

  // Designation is stored as its code; the save formData needs the full name.
  const [designationOptions, setDesignationOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  useEffect(() => {
    if (!isOpen || designationOptions.length > 0) return;
    eligibilityCheckServices
      .getDesignations()
      .then((resp: any) => {
        if (resp?.data?.status === "success") {
          setDesignationOptions(
            (resp.data.data || []).map((designation: any) => ({
              value: designation.genDesignation,
              label: designation.genDesgDesc,
            })),
          );
        }
      })
      .catch((error: any) => console.error("Error fetching designations:", error));
  }, [isOpen, designationOptions.length]);

  useEffect(() => {
    if (!isOpen) return;

    setRelationshipOptions(preloadedRelationshipOptions);
    setSalutationOptions(preloadedSalutationOptions);
    setNationalityOptions(preloadedNationalityOptions);
    setMaritalStatusOptions(preloadedMaritalStatusOptions);
    setCountryOptions(preloadedCountryOptions);

    const shouldFetchRelationship = preloadedRelationshipOptions.length === 0;
    const shouldFetchSalutation = preloadedSalutationOptions.length === 0;
    const shouldFetchNationality = preloadedNationalityOptions.length === 0;
    const shouldFetchMaritalStatus = preloadedMaritalStatusOptions.length === 0;
    const shouldFetchCountry = preloadedCountryOptions.length === 0;

    if (
      !shouldFetchRelationship &&
      !shouldFetchSalutation &&
      !shouldFetchNationality &&
      !shouldFetchMaritalStatus &&
      !shouldFetchCountry
    ) {
      setIsLoadingOptions(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoadingOptions(true);
      try {
        const [relRes, salRes, nationalityRes, maritalStatusRes, countryRes] =
          await Promise.allSettled([
            shouldFetchRelationship
              ? losServices.relationMaster()
              : Promise.resolve(null),
            shouldFetchSalutation
              ? dropdownService.getSalutations()
              : Promise.resolve(null),
            shouldFetchNationality
              ? dropdownService.getNationalityList()
              : Promise.resolve(null),
            shouldFetchMaritalStatus
              ? dropdownService.getMaritalStatusMaster()
              : Promise.resolve(null),
            shouldFetchCountry
              ? dropdownService.getCountries()
              : Promise.resolve(null),
          ]);

        if (
          !cancelled &&
          shouldFetchRelationship &&
          relRes.status === "fulfilled" &&
          relRes.value?.status === 200
        ) {
          const optionsData = relRes.value?.data?.data?.map((item: any) => ({
            label: item?.descInPennant,
            value: item?.codeInPennant,
          }));
          setRelationshipOptions(optionsData || []);
        }

        if (
          !cancelled &&
          shouldFetchSalutation &&
          salRes.status === "fulfilled" &&
          Array.isArray(salRes?.value?.data)
        ) {
          const salutationsOptions = salRes?.value?.data?.map((sal: any) => ({
            value: sal?.salutationCode,
            label: sal?.salutationDesc,
          }));
          setSalutationOptions(salutationsOptions || []);
        }

        if (
          !cancelled &&
          shouldFetchNationality &&
          nationalityRes.status === "fulfilled" &&
          Array.isArray(nationalityRes.value)
        ) {
          setNationalityOptions(nationalityRes.value);
        }

        if (
          !cancelled &&
          shouldFetchMaritalStatus &&
          maritalStatusRes.status === "fulfilled" &&
          Array.isArray(maritalStatusRes.value?.data)
        ) {
          const maritalStatusMasterOptions = maritalStatusRes?.value?.data
            .map((item: any) => ({
              value: String(item?.maritalStatusCode ?? ""),
              label: String(
                item?.maritalStatusName ?? item?.maritalStatusDesc ?? ""
              ),
            }))
            .filter((opt: Option) => Boolean(opt.value && opt.label));
          setMaritalStatusOptions(maritalStatusMasterOptions || []);
        }

        if (
          !cancelled &&
          shouldFetchCountry &&
          countryRes.status === "fulfilled" &&
          Array.isArray(countryRes?.value?.data)
        ) {
          const countryMasterOptions = countryRes?.value?.data
            .map((item: any) => ({
              value: String(item?.countryId ?? ""),
              label: String(item?.countryDesc ?? ""),
            }))
            .filter((opt: Option) => Boolean(opt.value && opt.label));
          setCountryOptions(countryMasterOptions || []);
        }
      } catch (e) {
        // Keep modal usable even if one dropdown fails
        console.error("Failed to load co-applicant dropdown options:", e);
      } finally {
        if (!cancelled) setIsLoadingOptions(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    preloadedRelationshipOptions,
    preloadedSalutationOptions,
    preloadedNationalityOptions,
    preloadedMaritalStatusOptions,
    preloadedCountryOptions,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setVerifiedPanSignature("");
    }
  }, [isOpen]);


  const validationSchema = useMemo(
    () => getValidationSchema({ ...fieldNames, ...employmentFieldNames, ...addressFieldNames }, nextCoApplicantNumber > 1 ? true : false),
    [fieldNames, employmentFieldNames, nextCoApplicantNumber]
  );

  const revalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formikBagRef = useRef<any>(null);

  useEffect(
    () => () => {
      if (revalidateTimerRef.current) {
        clearTimeout(revalidateTimerRef.current);
      }
    },
    []
  );

  const setFieldValueAndRevalidate = useCallback(
    (name: string, value: any, shouldValidate?: boolean) => {
      const bag = formikBagRef.current;
      if (!bag) return;

      bag.setFieldValue(name, value, shouldValidate);

      if (revalidateTimerRef.current) {
        clearTimeout(revalidateTimerRef.current);
      }
      revalidateTimerRef.current = setTimeout(() => {
        revalidateTimerRef.current = null;
        formikBagRef.current?.validateForm();
      }, 0);
    },
    []
  );

  const [activeTab, setActiveTab] = useState<number>(0);
  

  const basicFieldKeys = useMemo(() => [
    relationshipField,
    relationValueField,
    salutationField,
    firstNameField,
    lastNameField,
    genderField,
    dobField,
    panField,
    phoneField,
    emailField,
    nationalityField,
    maritalStatusField,
    countryOfBirthField,
    cityOfBirthField,
    payPartyField,
  ], [
    relationshipField,
    relationValueField,
    salutationField,
    firstNameField,
    lastNameField,
    genderField,
    dobField,
    panField,
    phoneField,
    emailField,
    nationalityField,
    maritalStatusField,
    countryOfBirthField,
    cityOfBirthField,
    payPartyField,
  ]);
  const employmentFieldKeys = useMemo(() => [isEmployedField, employmentTypeField], [isEmployedField, employmentTypeField]);
  const addressFieldKeys = useMemo(() => Object.values(addressFieldNames), [addressFieldNames]);

  const getPanSignature = (values: any) => {
    const pan = String(values[panField] || "")
      .trim()
      .toUpperCase();
    const dob = values[dobField]
      ? dayjs(values[dobField] as Date).format("YYYY-MM-DD")
      : "";
    return `${pan}|${dob}`;
  };

  const sanitizeNameInput = (value: string) =>
    String(value || "")
      .replace(/[^a-zA-Z\s]/g, "")
      .replace(/^\s+/, "");

  const fetchBirthCities = useCallback(async (countryValue: any) => {
    const normalizedCountry = getAutocompleteOptionLabel(countryValue)
      .trim()
      .replace(/\s+/g, " ");
    const normalizedCountryLower = normalizedCountry.toLowerCase();

    if (!normalizedCountry) {
      lastBirthCityCountryRef.current = "";
      setCityOfBirthOptions([]);
      setIsLoadingBirthCities(false);
      return;
    }

    if (lastBirthCityCountryRef.current.toLowerCase() === normalizedCountryLower) {
      return;
    }

    lastBirthCityCountryRef.current = normalizedCountry;
    const requestId = ++birthCityRequestIdRef.current;
    setIsLoadingBirthCities(true);

    try {
      const resp = await eligibilityCheckServices.getCitiesByCountryState(
        normalizedCountry
      );

      if (requestId !== birthCityRequestIdRef.current) {
        return;
      }

      if (resp.status === 200 && resp.data?.data) {
        const rawCities = Array.isArray(resp.data?.data)
          ? resp.data.data
          : typeof resp.data?.data === "object" && resp.data?.data !== null
            ? Object.values(resp.data.data)
            : [];

        const seenCities = new Set<string>();
        const locationOptions = rawCities
          .map((item: any) => {
            if (item && typeof item === "object") {
              const label = String(
                item?.cityName ??
                  item?.cityDesc ??
                  item?.name ??
                  item?.label ??
                  item?.value ??
                  ""
              )
                .trim()
                .replace(/\s+/g, " ");
              const value = String(
                item?.city ??
                  item?.cityCode ??
                  item?.value ??
                  item?.code ??
                  ""
              )
                .trim()
                .replace(/\s+/g, " ");

              if (!label && !value) {
                return null;
              }

              return {
                label: label || value,
                value: value || label,
              };
            }

            const normalizedCity = String(item ?? "")
              .trim()
              .replace(/\s+/g, " ");

            if (!normalizedCity) {
              return null;
            }

            return {
              label: normalizedCity,
              value: normalizedCity,
            };
          })
          .filter((city: any) => city?.label || city?.value)
          .filter((city: any) => {
            const key = `${String(city?.value || "").toLowerCase()}::${String(
              city?.label || ""
            ).toLowerCase()}`;
            if (seenCities.has(key)) {
              return false;
            }
            seenCities.add(key);
            return true;
          });

        setCityOfBirthOptions(locationOptions);
      } else {
        setCityOfBirthOptions([]);
      }
    } catch (error) {
      if (requestId === birthCityRequestIdRef.current) {
        setCityOfBirthOptions([]);
      }
      console.error("Failed to load city of birth options:", error);
    } finally {
      if (requestId === birthCityRequestIdRef.current) {
        setIsLoadingBirthCities(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      lastBirthCityCountryRef.current = "";
      birthCityRequestIdRef.current = 0;
      setCityOfBirthOptions([]);
      setIsLoadingBirthCities(false);
    }
  }, [isOpen]);

  const unformatNumber = (formattedValue: string): string => {
    // Allows decimal points but removes all commas
    return formattedValue.replace(/,/g, "");
  };

  const firstNonEmpty = (...values: any[]) =>
    values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") ?? "";

  const getOptionValue = (value: any) =>
    value && typeof value === "object" ? value?.value ?? value?.countryId ?? value?.label : value;

  const getOptionLabel = (value: any) =>
    value && typeof value === "object" ? value?.label ?? value?.countryDesc ?? value?.value : value;

  const resolveCountryId = (value: any) => {
    const selectedValue = String(getOptionValue(value) ?? "").trim();
    const selectedLabel = String(getOptionLabel(value) ?? "").trim();
    return firstNonEmpty(
      selectedValue && countryOptions.find((country: any) => String(country?.value) === selectedValue)?.value,
      selectedLabel && countryOptions.find((country: any) => String(country?.label) === selectedLabel)?.value,
      selectedValue,
      "IN"
    );
  };

  const saveEmploymentDetilsToProcessio = async (values: any, coAppNum: number, applicationDisplayCode: any, resApplicantId: any) => {
    if (values[`employmentCoApplicant${coAppNum}isEmployed`] === "Yes") {
          let addressTypeDescriptionVal = "";
          let addressTypeIdVal = "";
          const employmentType =
            values?.[`employmentCoApplicant${coAppNum}employmentType`] || "";
          const isCompanyAddressRequired = !["farmer", "others", "other"].includes(
            String(employmentType).toLowerCase()
          );
          // Salaried--CODE--OFFICE---AddrTypeDesc---OFFICE / BUSINESS ADDRESS
          // SEP/SENP---CODE--BUS---AddrTypeDesc---BUSINESS ADDRESS
          if (
            employmentType === "Salaried"
          ) {
              addressTypeDescriptionVal = "OFFICE / BUSINESS ADDRESS";
              addressTypeIdVal = "OFFICE";
          } else if (
            employmentType === "SEP" ||
            employmentType === "SENP"
          ) {
              addressTypeDescriptionVal = "BUSINESS ADDRESS";
              addressTypeIdVal = "BUS";
            }
          let employmentDetailsPayload: any = {
            appEmploymentId: null,
            trackingId: trackingId,
            isAddingViaApplicationFlow: true,
            applicationDisplayCode: applicationDisplayCode,
            applicationId: Number(applicationId) || 0,
            queryId: "",
            // startDate: values?.employmentStartDate
            // ? new Date(values.employmentStartDate).getTime()
            // : null,
            endDate: values?.employmentEndDate
              ? new Date(values.employmentEndDate).getTime()
                : "",
		            companyAddress: isCompanyAddressRequired ? {
		              applicationDisplayCode: applicationDisplayCode|| "",
		              addressLine1: values[employmentFieldNames.companyAddress1] || "",
		              addressLine2: values[employmentFieldNames.companyAddress2] || "",
	              cityId: firstNonEmpty(companyCodeData?.cityId, companyCodeData?.city),
	              cityName: values[employmentFieldNames.city],
	              countryId: firstNonEmpty(companyCodeData?.countryId, resolveCountryId(values[employmentFieldNames.companyCountry])),
	              countryName: getOptionLabel(values[employmentFieldNames.companyCountry]),
	              landmark: values[employmentFieldNames.landmark],
	              locality: values[employmentFieldNames.companyAddress3],
	              pincode: values[employmentFieldNames.pincode],
	              pincodeId: firstNonEmpty(companyCodeData?.pincodeId, companyCodeData?.pinCodeId),
	              stateId: firstNonEmpty(companyCodeData?.stateId, companyCodeData?.stateCode),
		              stateName: values[employmentFieldNames.state],
		              addressTypeDescription: addressTypeDescriptionVal,
		              addressTypeId: addressTypeIdVal,
	            } : null,
            companyName: values[employmentFieldNames.companyName] || "",
            currentWorkExpMonths:
              Number(values[employmentFieldNames.employmentMonth]) || 0,
            currentWorkExpYears:
              Number(values[employmentFieldNames.employmentYear]) || 0,
            designation: values[employmentFieldNames.designation] || "",
            //designationId: values?.employmentDesignationId || "",
	             employmentTypeDesc:
              ['others', 'other'].includes(employmentType?.toLowerCase())
                ? 'Others'
                : employmentType || '',             
                employmentTypeId:
                employmentType === "Others" || employmentType === "Other"
                  ? "OTHER"
                  : employmentType,
            currentlyWorking:
              typeof values?.employmentCurrentlyWorking === "boolean"
                ? values?.employmentCurrentlyWorking
                : true,
            isStudentEarningFlag: true,
            monthlyIncome: values[employmentFieldNames?.monthlyIncome] ? Number(unformatNumber(values[employmentFieldNames.monthlyIncome])) : 0,
            monthlyObligations:values [employmentFieldNames.monthlyObligation] ? 
              Number(unformatNumber(values[employmentFieldNames.monthlyObligation])) : null,
            payParty:
              coAppNum === 1 ? true : values[payPartyField] === "Yes",
            eventName: `COAPPLICANT${coAppNum}_EMPLOYMENT_DETAILS_SAVED`,
            userApplicantType: `Co-Applicant ${coAppNum}`,
            incomeTobeConsideredFlag:
                      values[`basicCoApplicant${coAppNum}PayParty`] === "Yes" || coAppNum===1
                        ? true
                        : false,
          };
          if (resApplicantId) {
            employmentDetailsPayload.applicantId = Number(resApplicantId);
          }
   
        return await losServices.saveEmploymentDetails(employmentDetailsPayload);
    } else {
      let employmentDetailsPayloadOne: any = {
          trackingId: trackingId,
          eventName: `COAPPLICANT${coAppNum}_EMPLOYMENT_DETAILS_SAVED`,
          appEmploymentId: null,
          isAddingViaApplicationFlow: true,
          applicationDisplayCode: applicationDisplayCode,
          applicationId: Number(applicationId) || 0,
	          queryId: "",
	          endDate: "",
            companyAddress: null,
	          employmentTypeDesc: "student",
          employmentTypeId: "student",
          currentlyWorking: false,
          isStudentEarningFlag: false,
          incomeTobeConsideredFlag:
            values[`basicCoApplicant${coAppNum}PayParty`] === "Yes" || coAppNum===1
              ? true
              : false,
          userApplicantType: `Co-Applicant ${coAppNum}`,
      };
   
      if (resApplicantId) {
        employmentDetailsPayloadOne.applicantId = Number(
          resApplicantId
        );
      }
      return await losServices.saveEmploymentDetails(employmentDetailsPayloadOne);
    }
  }

  const handleInvokPinCodeApi = async (
      zipCode: string,
      fieldType: string,
      stateCode: string,
      pinCodeId: string,
      city: string,
      currentsameAsPermanent: boolean,
    ) => {
      try {
        const resp = await eligibilityCheckServices.getPinCode(zipCode);

        if (resp.status === 200) {
          if(fieldType === "permanent") {
            if(resp.data.data?.[0].city) {
              city = resp.data.data?.[0].city
            }
            if(resp.data.data?.[0].pinCodeId) {
              pinCodeId = resp.data.data?.[0].pinCodeId
            }
            
            const permanentData = { ...resp.data.data?.[0], stateCode, pinCodeId, city };
            if(currentsameAsPermanent) {
              setPermanentCodeData(permanentData);
              setCurrentCodeData(permanentData);
            } else {
               setPermanentCodeData(permanentData);
            }
          } else if(fieldType === "current"){
            if(resp.data.data?.[0].city) {
              city = resp.data.data?.[0].city
            }
            if(resp.data.data?.[0].pinCodeId) {
              pinCodeId = resp.data.data?.[0].pinCodeId
            }
            let currentData = { ...resp.data.data?.[0], stateCode, pinCodeId, city };
            if(currentsameAsPermanent) {
              currentData = { ...resp.data.data?.[0], stateCode: permanentCodeData.stateCode,pinCodeId:  permanentCodeData.pinCodeId, city: permanentCodeData.city };
              setPermanentCodeData(currentData);
              setCurrentCodeData(currentData);
            } else {
               setCurrentCodeData(currentData);
            }
          } else if(fieldType === "employment") {
            if(resp.data.data?.[0].city) {
              city = resp.data.data?.[0].city
            }
            if(resp.data.data?.[0].pinCodeId) {
              pinCodeId = resp.data.data?.[0].pinCodeId
            }
            const companyData = { ...resp.data.data?.[0], stateCode, pinCodeId, city };
            setCompanyCodeData(companyData)
          }
        }
      } catch(err) {
        console.log("error", err)
      }
    }

  
  const saveAddressDetailsToProcessio = async (values: any, coAppNum: number, applicantId: string) => {
    const demographicDetailsPayload: any = {
        applicantId: Number(applicantId) || 0,
        trackingId: trackingId,
        isPermanentNotSameAsCurrentFlag:
          !values[addressFieldNames.currentsameAsPermanent],
        permanentAddress: {
          addressLine1: values[addressFieldNames.permanentAddressLine1] || "",
          addressLine2: values[addressFieldNames.permanentAddressLine2] || "",
          addressLine3: values[addressFieldNames.permanentAddressLine3] || "",
          addressTypeDescription: "PERMANENT ADDRESS",
          addressTypeId: "PER",
          cityId: permanentCodeData?.city || "",
          cityName: values[addressFieldNames.permanentCity] || "",
          countryId:
            countryOptions?.find(
            (country: any) =>
              country.label === (typeof values[addressFieldNames.permanentCountry] === "object" ? values[addressFieldNames.permanentCountry].label : values[addressFieldNames.permanentCountry])
          )?.value || "IN",
          countryName: typeof values[addressFieldNames.permanentCountry] === "object" ? values[addressFieldNames.permanentCountry].label : values[addressFieldNames.permanentCountry],
          landmark: values[addressFieldNames.permanentAddressLine2] || "",
          locality: values[addressFieldNames.permanentAddressLine3] || "",
          // ownershipStatusValue:
          //   values.permanentOwnershipStatusValue || "",
          ownershipStatusName: values[addressFieldNames.permanentOwnershipStatus] || "",
          ownershipStatusValue: handleOwnershipStatusValue(values[addressFieldNames.permanentOwnershipStatus]),
          pincode: String(values[addressFieldNames.permanentPincode]) || "",
          pincodeId: permanentCodeData?.pinCodeId || "",
          stateId: permanentCodeData?.stateCode || "",
          stateName: values[addressFieldNames.permanentState] || "",
          yearsAtCurrentAddress:
            String(values[addressFieldNames.permanentYearsInCurrentAddress]) || "",
          yearsInCurrentCity:
            String(values[addressFieldNames.permanentYearsInCurrentCity]) || "",
        },
        currentAddress: {
          addressLine1: values[addressFieldNames.currentAddressLine1] || "",
          addressLine2: values[addressFieldNames.currentAddressLine2] || "",
          addressLine3: values[addressFieldNames.currentAddressLine3] || "",
          addressTypeDescription: "RESIDENCE ADDRESS",
          addressTypeId: "CURRES",
          cityId: currentCodeData?.city || "",
          cityName: values[addressFieldNames.currentCity] || "",
          countryId:
            countryOptions?.find(
              (country: any) =>
                country.label === (typeof values[addressFieldNames.currentCountry] === "object" ? values[addressFieldNames.currentCountry].label : values[addressFieldNames.currentCountry])
              )?.value || "IN",
          countryName: typeof values[addressFieldNames.currentCountry] === "object" ? values[addressFieldNames.currentCountry].label : values[addressFieldNames.currentCountry],
          landmark: values[addressFieldNames.currentAddressLine2] || "",
          locality: values[addressFieldNames.currentAddressLine3] || "",
          monthsAtCurrentAddress: values.monthsAtCurrentAddress || 0,
          monthsInCurrentCity: values.monthsInCurrentCity || 0,
          // ownershipStatusValue:
          //   values.currentOwnershipStatusValue || "",
          ownershipStatusName: values[addressFieldNames.currentOwnershipStatus] || "",
          ownershipStatusValue: handleOwnershipStatusValue(values[addressFieldNames.currentOwnershipStatus]),
          pincode: String(values[addressFieldNames.currentPincode]) || "",
          pincodeId: currentCodeData?.pinCodeId || "",
          stateId: currentCodeData?.stateCode || "",
          stateName: values[addressFieldNames.currentState] || "",
          yearsAtCurrentAddress:
            String(values[addressFieldNames.currentYearsInCurrentAddress]) || "",
          yearsInCurrentCity:
            String(values[addressFieldNames.currentYearsInCurrentCity]) || "",
          },
          eventName: `COAPPLICANT${coAppNum}_DEMOGRAPHIC_DETAILS_ADDED`,
          userApplicantType:`Co-Applicant ${coAppNum}`,
        };
      const demographicDetailsResp =
          await losServices.createUpdateApplicantDemographicDetails(demographicDetailsPayload);
  }

  return (
    <ModalPopup
      isOpen={isOpen}
      onClose={() => {
        setIsShowWarning(true);
      }}
      title="Add Co-Applicant"
      maxWidth="lg"
    >
      <Box>
        {isLoadingOptions ? (
          <Typography sx={{ px: 2, py: 2 }}>Loading co-applicant fields...</Typography>
        ) : (
          <Formik
            innerRef={formikBagRef}
            initialValues={{
              [relationshipField]: "",
              [relationValueField]: "",
              [salutationField]: "",
              [firstNameField]: "",
              [lastNameField]: "",
              [genderField]: "",
              [dobField]: null as Date | null,
              [panField]: "",
              [phoneField]: "",
              [emailField]: "",
              [nationalityField]: "",
              [maritalStatusField]: "",
              [countryOfBirthField]: "",
              [cityOfBirthField]: "",
              [payPartyField]: "",
              [isEmployedField]: "Yes",
              [employmentTypeField]: "",
              // address fields
              [addressFieldNames.permanentOwnershipStatus]: "",
              [addressFieldNames.permanentYearsInCurrentAddress]: "",
              [addressFieldNames.permanentYearsInCurrentCity]: "",
              [addressFieldNames.permanentAddressLine1]: "",
              [addressFieldNames.permanentAddressLine2]: "",
              [addressFieldNames.permanentAddressLine3]: "",
              [addressFieldNames.permanentPincode]: "",
              [addressFieldNames.permanentCity]: "",
              [addressFieldNames.permanentState]: "",
              [addressFieldNames.permanentCountry]: "",
              [addressFieldNames.currentsameAsPermanent]: true,
              [addressFieldNames.currentOwnershipStatus]: "",
              [addressFieldNames.currentYearsInCurrentAddress]: "",
              [addressFieldNames.currentYearsInCurrentCity]: "",
              [addressFieldNames.currentAddressLine1]: "",
              [addressFieldNames.currentAddressLine2]: "",
              [addressFieldNames.currentAddressLine3]: "",
              [addressFieldNames.currentPincode]: "",
              [addressFieldNames.currentCity]: "",
              [addressFieldNames.currentState]: "",
              [addressFieldNames.currentCountry]: "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
               if(values[isEmployedField] === "Yes" && !values[employmentTypeField]) {
                setEmpTypeError("Employment Type field is required");
                return;
              }
              setSubmitting(true);
              setIsSubmitting(true);
              try {
                const genderMap = mapGender() as Array<{ code: string; name: string }>;
                const genderId =
                  genderMap.find(
                    (g) =>
                      g.name.toLowerCase() ===
                      String(values[genderField] || "").toLowerCase()
                  )?.code || "";

                const selectedSalutation = salutationOptions.find(
                  (opt) =>
                    String(opt.value) === String(values[salutationField] || "")
                );
                const selectedRelationship = relationshipOptions.find(
                  (opt) =>
                    String(opt.value) === String(values[relationshipField] || "")
                );
                const selectedNationality = resolveAutocompleteOption(
                  values[nationalityField],
                  nationalityOptions
                );
                const selectedMaritalStatus = resolveAutocompleteOption(
                  values[maritalStatusField],
                  maritalStatusOptions
                );
                const selectedCountryOfBirth = resolveAutocompleteOption(
                  values[countryOfBirthField],
                  countryOptions
                );

                // 1) GET /api/v1/product/co-applicant-fields
                const coApplicantFieldsResp: any =
                  await losServices.getCoApplicantFields();
                const rawFields = coApplicantFieldsResp?.data?.data;
                const coApplicantFields = Array.isArray(rawFields)
                  ? rawFields
                  : Array.isArray(rawFields?.formFields)
                    ? rawFields.formFields
                    : [];

                const coApplicantType = `Co-Applicant ${nextCoApplicantNumber}`;
                const rollbackCoApplicant = async (formFieldIds: number[]) => {
                  if (!Array.isArray(formFieldIds) || formFieldIds.length === 0) {
                    return;
                  }
                  try {
                    await losServices.deleteUserFields({
                      trackingId,
                      form_field_ids: formFieldIds,
                    });
                  } catch (deleteErr) {
                    console.error("Error calling delete-user-fields API", deleteErr);
                  }
                };

                let storeUserFieldsResp: any;
                if (nextCoApplicantNumber > 1) {
                  // 2) POST /api/v1/product/store-user-fields (before save so formFieldIds match DB)
                  const storeUserFieldsPayload = buildStoreUserFieldsPayload({
                    nextCoApplicantNumber,
                    trackingId,
                    relationshipOptions,
                    nationalityOptions,
                    maritalStatusOptions,
                    countryOptions,
                  });
                  storeUserFieldsResp = await losServices.storeUserFields(
                    storeUserFieldsPayload
                  );
                }

                const storeUserFieldsData = storeUserFieldsResp?.data?.data;
                const findFieldId = createFieldIdResolver(
                  coApplicantFields,
                  nextCoApplicantNumber,
                  Array.isArray(storeUserFieldsData) ? storeUserFieldsData : null
                );

                const normalized = normalizeValues(values, fieldNames, maritalStatusOptions);
                const currentPanSignature = getPanSignature(values);
                const isPanVerifiedForCurrentInputs =
                  Boolean(verifiedPanSignature) &&
                  verifiedPanSignature === currentPanSignature;
                const saveFormData = buildSaveFormData({
                  fields: fieldNames,
                  findFieldId,
                  values,
                  normalized,
                  isPanVerified: isPanVerifiedForCurrentInputs,
                  nextCoApplicantNumber
                });
                const saveAddressFormData = buildAddressSaveFormData({
                  fields: addressFieldNames,
                  findFieldId,
                  values
                });
                const saveEmpFormData = buildEmpSaveFormData({
                  fields: employmentFieldNames,
                  findFieldId,
                  values,
                  designations: designationOptions,
                })
                const createdFormFieldIds: number[] = (
                  storeUserFieldsResp?.data?.data || []
                )
                  .map((item: any) => Number(item?.id))
                  .filter((id: number) => Number.isFinite(id) && id > 0);

                // idDto: resolved-ID mirror of the raw formData above, for
                // this save-coapplicant call into OUR OWN DB (not the
                // processio calls further down, which build their own
                // payloads independently). See buildCoApplicantSaveIdDto for
                // why: built entirely from what's already synchronously
                // available (dropdown option lists, permanentCodeData/
                // currentCodeData/companyCodeData — this modal's own
                // per-section pincode-lookup cache) — no new API calls.
                // Unlike the tab-by-tab editors, this modal saves
                // basic+address+employment all at once, so every idDto
                // section is populated here in a single call. Note:
                // cityOfBirthOptions has no fetch-fallback in this file (the
                // other editors' more aggressive birthCityId retry isn't
                // available here) — if it hasn't loaded yet, birthCityId
                // just comes back blank, same as everything else in idDto.
                const idDto = buildCoApplicantSaveIdDto({
                  // This modal saves basic+address+employment all in one
                  // shot (oneTimeSave), so every idDto section is always
                  // relevant here — unlike the tab-by-tab editors (which now
                  // only fill the single section they're actually saving),
                  // "all" fills every section in this one call.
                  stage: "all",
                  getField: (section, suffix) => {
                    switch (section) {
                      case "basic":
                        switch (suffix) {
                          case "Gender": return values[genderField];
                          case "salutation": return values[salutationField];
                          case "RelationWithStudent": return values[relationshipField];
                          case "Nationality": return values[nationalityField];
                          case "MaritalStatus": return values[maritalStatusField];
                          case "CountryOfBirth": return values[countryOfBirthField];
                          case "CityOfBirth": return values[cityOfBirthField];
                          default: return undefined;
                        }
                      case "permanent":
                        switch (suffix) {
                          case "OwnershipStatus": return values[addressFieldNames.permanentOwnershipStatus];
                          case "Country": return values[addressFieldNames.permanentCountry];
                          default: return undefined;
                        }
                      case "current":
                        switch (suffix) {
                          case "OwnershipStatus": return values[addressFieldNames.currentOwnershipStatus];
                          case "Country": return values[addressFieldNames.currentCountry];
                          default: return undefined;
                        }
                      case "employment":
                        switch (suffix) {
                          case "employmentType": return values[employmentTypeField];
                          case "designation": return values[employmentFieldNames.designation];
                          default: return undefined;
                        }
                      case "company":
                        switch (suffix) {
                          case "Country": return values[employmentFieldNames.companyCountry];
                          default: return undefined;
                        }
                      default:
                        return undefined;
                    }
                  },
                  countryOptions,
                  maritalStatusOptions,
                  nationalityOptions,
                  cityOptionsForBirth: cityOfBirthOptions,
                  coApplicantZipCodeData: {
                    modal: {
                      permanent: permanentCodeData,
                      current: currentCodeData,
                      company: companyCodeData,
                    },
                  },
                  coAppKey: "modal",
                  designations: designationOptions,
                });

                // 3) POST /api/v1/applicant/save-coapplicant
                let saveCoApplicantResp: any;
                try {
                  saveCoApplicantResp = await losServices.saveCoApplicantFormData({
                    trackingId,
                    mobileNumber: normalized.phone,
                    email: normalized.email,
                    coApplicantType,
                    formData: [...saveFormData, ...saveAddressFormData, ...saveEmpFormData],
                    isAadhaarSeeded: false,
                    oneTimeSave: true,
                    idDto,
                  });
                  const isSaveSuccess =
                    saveCoApplicantResp?.status >= 200 &&
                    saveCoApplicantResp?.status < 300 &&
                    saveCoApplicantResp?.data?.success !== false;
                  if (!isSaveSuccess) {
                    throw saveCoApplicantResp;
                  }
                } catch (saveErr) {
                  // Critical rollback when save-coapplicant fails
                  await rollbackCoApplicant(createdFormFieldIds);
                  throw saveErr;
                }

                // Only reached on a successful save-coapplicant response
                dispatch(
                  setStatusPopup({
                    status: "success",
                    message: "Co-Applicant Saved Successfully",
                  })
                );
                if (typeof onSuccess === "function") {
                  await onSuccess();
                }
                onClose();
              } catch (e: any) {
                dispatch(
                  setStatusPopup({
                    status: "error",
                    message:
                      e?.response?.data?.message ||
                      e?.data?.message ||
                      "Failed to create co-applicant.",
                  })
                );
              } finally {
                setIsSubmitting(false);
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              submitCount,
              handleSubmit,
              setTouched,
              setFieldValue,
              setFieldTouched,
              validateForm
            }) => {
              const currentPanSignature = getPanSignature(values);
              const isPanVerifiedForCurrentInputs =
                Boolean(verifiedPanSignature) &&
                verifiedPanSignature === currentPanSignature;
                            
                // Remove hidden payParty field errors/values for co-applicant 1
                if (nextCoApplicantNumber === 1) {
                  if (payPartyField in errors) {
                    delete (errors as any)[payPartyField];
                  }
                  if (payPartyField in values) {
                    delete (values as any)[payPartyField];
                  }
                }

                const handleChangeTab = async (_: any, v: number) => {
                  if (v === activeTab) return;

                  const formErrors = await validateForm();
                  const keys = Object.keys(formErrors || {});
                  // if moving forward, ensure intermediate tabs have no blocking errors
                  if (v > activeTab) {
                    // check basic
                    if (v >= 1 && keys.some((k) => basicFieldKeys.includes(k))) {
                      const touchedObj: any = {};
                      basicFieldKeys.forEach((k) => (touchedObj[k] = true));
                      setTouched(touchedObj);
                      return;
                    }
                    // check address if moving to 2 or beyond
                    if (v >= 2 && keys.some((k) => addressFieldKeys.includes(k))) {
                      const touchedObj: any = {};
                      addressFieldKeys.forEach((k: any) => (touchedObj[k] = true));
                      setTouched(touchedObj);
                      return;
                    }
                  }
                  
                  setActiveTab(v);
                };

              return (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formErrors = await validateForm();
                    const errorKeys = Object.keys(formErrors || {});
                    if (errorKeys.some((key) => key.includes("basic"))) {
                      setActiveTab(0);
                    } else if (errorKeys.some((key) => key.includes("address"))) {
                      setActiveTab(1);
                    } else if (errorKeys.some((key) => key.includes("employment"))) {
                      setActiveTab(2);
                    }
                    setTouched({
                      [relationshipField]: true,
                      ...(values[relationshipField] === "OTHERS"
                        ? { [relationValueField]: true }
                        : {}),
                      [salutationField]: true,
                      [firstNameField]: true,
                      [lastNameField]: true,
                      [genderField]: true,
                      [dobField]: true,
                      [panField]: true,
                      [phoneField]: true,
                      [emailField]: true,
                      [nationalityField]: true,
                      [maritalStatusField]: true,
                      [countryOfBirthField]: true,
                      [cityOfBirthField]: true,
                      ...(nextCoApplicantNumber > 1 ? { [payPartyField]: true } : {}),
                       // address fields
                      [addressFieldNames.permanentOwnershipStatus]: true,
                      [addressFieldNames.permanentYearsInCurrentAddress]: true,
                      [addressFieldNames.permanentYearsInCurrentCity]: true,
                      [addressFieldNames.permanentAddressLine1]: true,
                      [addressFieldNames.permanentAddressLine2]: true,
                      [addressFieldNames.permanentAddressLine3]: true,
                      [addressFieldNames.permanentPincode]: true,
                      [addressFieldNames.permanentCity]: true,
                      [addressFieldNames.permanentState]: true,
                      [addressFieldNames.permanentCountry]: true,
                      [addressFieldNames.currentsameAsPermanent]: true,
                      [addressFieldNames.currentOwnershipStatus]: true,
                      [addressFieldNames.currentYearsInCurrentAddress]: true,
                      [addressFieldNames.currentYearsInCurrentCity]: true,
                      [addressFieldNames.currentAddressLine1]: true,
                      [addressFieldNames.currentAddressLine2]: true,
                      [addressFieldNames.currentAddressLine3]: true,
                      [addressFieldNames.currentPincode]: true,
                      [addressFieldNames.currentCity]: true,
                      [addressFieldNames.currentState]: true,
                      [addressFieldNames.currentCountry]: true,
                      [isEmployedField]: true,
                      [employmentTypeField]: true,
                    });
                    handleSubmit(e);
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Tabs
                        value={activeTab}
                        onChange={handleChangeTab}
                        variant={isXsOrSm ? "scrollable" : "standard"}
                        {...(isXsOrSm && {
                          scrollButtons: "auto",
                          allowScrollButtonsMobile: false,
                        })}
                        sx={{
                          mb: 2,
                          flexGrow: 1,
                          "& .MuiTabs-indicator": {
                            backgroundColor: "#00b3b5",
                          },
                          "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#616161",
                            "&.Mui-selected": {
                              color: "#00b3b5",
                            },
                          },
                        }}
                    >
                      <Tab label="Basic Details" />
                      <Tab label="Address Details" />
                      <Tab label="Employment Details" />
                    </Tabs>
                    {!isXsOrSm && <Box sx={{ ml: 2 }}>
                      <PrimaryButton
                          type="submit"
                          disabled={disabled || isSubmitting || isLoadingOptions || isPincodeLookupInProgress}
                          sx={{ px: 4 }}
                        >
                          {isSubmitting ? "Saving..." : "Save"}
                      </PrimaryButton>
                    </Box>}
                  </Box>

                  {activeTab === 0 && (
                    <>
                      
                    <Grid container spacing={2} sx={{ px: 1, pb: 1 }}>
                    {/*
                    Formik only marks `touched` when using `handleBlur`.
                    Since many inputs here are wired via `setFieldValue`,
                    we also show errors when the user attempts submit.
                  */}
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Relationship with Student{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <AutoCompleteOptions
                          label=""
                          options={relationshipOptions}
                          value={
                            relationshipOptions.find(
                              (opt) =>
                                String(opt.value) === String(values[relationshipField] || "")
                            ) || null
                          }
                          isMandatory={false}
                          form={{
                            values,
                            setFieldValue: setFieldValueAndRevalidate,
                            setFieldTouched,
                          }}
                          fieldProps={{ name: relationshipField }}
                          onChange={(opt: any) => {
                            const nextValue = opt ? opt.value : "";
                            setFieldValueAndRevalidate(relationshipField, nextValue);
                            if (nextValue !== "OTHERS") {
                              setFieldValueAndRevalidate(relationValueField, "");
                              setFieldTouched(relationValueField, false, false);
                            }
                          }}
                          placeholder="Select Relationship with Student"
                          error={
                            (submitCount > 0 &&
                              !!(errors as any)[relationshipField]) ||
                            (!!(touched as any)[relationshipField] &&
                              !!(errors as any)[relationshipField])
                          }
                          helperText={
                            (submitCount > 0 ||
                              (touched as any)[relationshipField]) &&
                              (errors as any)[relationshipField]
                              ? String((errors as any)[relationshipField])
                              : ""
                          }
                        />
                      </Box>
                    </Grid>

                    {values[relationshipField] === "OTHERS" && (
                      <Grid item xs={12} sm={4}>
                        <Box>
                          <label className="text-lable" style={{ display: "block" }}>
                            Specify Relationship <span style={{ color: "red" }}>*</span>
                          </label>
                          <Field name={relationValueField}>
                            {({ field: f, meta, form }: any) => (
                              <InputField
                                {...f}
                                fullWidth
                                placeholder="Enter relationship"
                                variant="outlined"
                                autoComplete="new-password"
                                inputProps={{ maxLength: 50 }}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const sanitized = e.target.value
                                    .replace(/[^A-Za-z\s]/g, "")
                                    .slice(0, 50);
                                  setFieldValueAndRevalidate(relationValueField, sanitized);
                                }}
                                error={
                                  (form.submitCount > 0 || meta.touched) && !!meta.error
                                }
                                helperText={
                                  (form.submitCount > 0 || meta.touched) && meta.error
                                    ? meta.error
                                    : ""
                                }
                              />
                            )}
                          </Field>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          How should we address you? (Mr./Ms./etc.){" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <SelectOptions
                          label=""
                          value={(values as any)[salutationField] as
                            | string
                            | number
                            | undefined}
                          options={salutationOptions}
                          isMandatory={false}
                          placeholder="Select salutation"
                          onChange={(val) =>
                            setFieldValueAndRevalidate(salutationField, val)
                          }
                          error={
                            (submitCount > 0 && !!(errors as any)[salutationField]) ||
                            (!!(touched as any)[salutationField] &&
                              !!(errors as any)[salutationField])
                          }
                          helperText={
                            (submitCount > 0 ||
                              (touched as any)[salutationField]) &&
                              (errors as any)[salutationField]
                              ? String((errors as any)[salutationField])
                              : ""
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Co-Applicant First Name{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <Field name={firstNameField}>
                          {({ field: f, meta, form }: any) => (
                            <InputField
                              {...f}
                              fullWidth
                              placeholder="Enter Co-Applicant First Name"
                              variant="outlined"
                              inputmode="text"
                              autoComplete="new-password"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValueAndRevalidate(
                                  firstNameField,
                                  sanitizeNameInput(e.target.value)
                                );
                              }}
                              error={
                                (form.submitCount > 0 || meta.touched) &&
                                !!meta.error
                              }
                              helperText={
                                (form.submitCount > 0 || meta.touched) &&
                                  meta.error
                                  ? meta.error
                                  : ""
                              }
                            />
                          )}
                        </Field>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Co-Applicant Last Name{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <Field name={lastNameField}>
                          {({ field: f, meta, form }: any) => (
                            <InputField
                              {...f}
                              fullWidth
                              placeholder="Enter Co-Applicant Last Name"
                              variant="outlined"
                              inputmode="text"
                              autoComplete="new-password"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValueAndRevalidate(
                                  lastNameField,
                                  sanitizeNameInput(e.target.value)
                                );
                              }}
                              error={
                                (form.submitCount > 0 || meta.touched) &&
                                !!meta.error
                              }
                              helperText={
                                (form.submitCount > 0 || meta.touched) &&
                                  meta.error
                                  ? meta.error
                                  : ""
                              }
                            />
                          )}
                        </Field>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Gender <span style={{ color: "red" }}>*</span>
                        </label>
                        <SelectOptions
                          label=""
                          value={(values as any)[genderField] as
                            | string
                            | number
                            | undefined}
                          options={genderOptions}
                          isMandatory={false}
                          placeholder="Select gender"
                          onChange={(val) =>
                            setFieldValueAndRevalidate(genderField, val)
                          }
                          error={
                            (submitCount > 0 && !!(errors as any)[genderField]) ||
                            (!!(touched as any)[genderField] &&
                              !!(errors as any)[genderField])
                          }
                          helperText={
                            (submitCount > 0 ||
                              (touched as any)[genderField]) &&
                              (errors as any)[genderField]
                              ? String((errors as any)[genderField])
                              : ""
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Date of Birth <span style={{ color: "red" }}>*</span>
                        </label>
                        <BasicDatePicker
                          label=""
                          required={false}
                          value={
                            values[dobField] ? dayjs(values[dobField] as Date) : null
                          }
                          maxDate={dayjs()}
                          onChange={(date) =>
                            setFieldValueAndRevalidate(dobField, date)
                          }
                          error={
                            (submitCount > 0 && !!(errors as any)[dobField]) ||
                            (!!(touched as any)[dobField] &&
                              !!(errors as any)[dobField])
                          }
                          helperText={
                            (submitCount > 0 || (touched as any)[dobField]) &&
                              (errors as any)[dobField]
                              ? String((errors as any)[dobField])
                              : ""
                          }
                          onBlur={() => {
                            setFieldTouched(dobField, true);
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          PAN <span style={{ color: "red" }}>*</span>
                        </label>
                        <Field name={panField}>
                          {({ field: f, meta, form }: any) => (
                            <InputField
                              {...f}
                              fullWidth
                              placeholder="Enter PAN"
                              variant="outlined"
                              value={f.value}
                              onChange={(e) => {
                                const v = e.target.value.toUpperCase().slice(0, 10);
                                setFieldValueAndRevalidate(panField, v);
                              }}
                              onBlur={f.onBlur}
                              inputProps={{
                                maxLength: 10,
                                style: { textTransform: "uppercase" },
                                autoComplete: "off",
                              }}
                              InputProps={{
                                endAdornment: (
                                  <Button
                                    type="button"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();

                                      if (isPanVerifying) return;
                                      if (isPanVerifiedForCurrentInputs) return;

                                      const panVal = String(values[panField] || "")
                                        .trim()
                                        .toUpperCase();
                                      // Check one scenario at a time so only one message shows under the PAN field
                                      const panVerifyError = !values[dobField]
                                        ? "Please enter your Date of Birth before verifying your PAN."
                                        : !panVal
                                          ? "Please enter your PAN to proceed with verification."
                                          : !PAN_REGEX.test(panVal)
                                            ? "Please enter a valid 10-digit PAN number."
                                            : "";
                                      if (panVerifyError) {
                                        form.setFieldTouched(panField, true, false);
                                        form.setFieldError(panField, panVerifyError);
                                        return;
                                      }

                                      setIsPanVerifying(true);
                                      try {
                                        const piiPublicKey =
                                          await fetchSanitizedPiiPublicKey();
                                        if (!piiPublicKey) {
                                          dispatch(
                                            setStatusPopup({
                                              status: "error",
                                              message:
                                                "Unable to load encryption key. Please try again.",
                                            })
                                          );
                                          return;
                                        }

                                        const fullName = `${String(values[firstNameField] || "").trim()} ${String(values[lastNameField] || "").trim()}`.trim();
                                        const payload = await encryptData(
                                          {
                                            panNumber: panVal,
                                            fullName,
                                            dob: values[dobField]
                                              ? new Date(
                                                values[dobField] as Date
                                              ).toLocaleDateString("en-GB")
                                              : "",
                                            trackingId,
                                            applicantType: `Co-Applicant ${nextCoApplicantNumber}`,
                                          },
                                          ["panNumber"],
                                          piiPublicKey
                                        );
                                        const resp: any =
                                          await applicantDetailsServices.verifyPan(
                                            payload
                                          );

                                        if (resp?.status === 200) {
                                          if (
                                            String(
                                              resp?.data?.data?.dateOfBirth || ""
                                            ).toLowerCase() === "invalid"
                                          ) {
                                            setVerifiedPanSignature("");
                                            form.setFieldTouched(panField, true, false);
                                            form.setFieldError(
                                              panField,
                                              "We couldn't verify your PAN with the provided Date of Birth. Please verify your details and re-enter them."
                                            );
                                          } else {
                                            setVerifiedPanSignature(
                                              getPanSignature(values)
                                            );
                                            dispatch(
                                              setStatusPopup({
                                                status: "success",
                                                message: "PAN verified successfully.",
                                              })
                                            );
                                          }
                                        } else if (resp?.status === 400) {
                                          dispatch(
                                            setStatusPopup({
                                              status: "error",
                                              message:
                                                "Uh-oh! We are experiencing some technical issues fetching the co-applicant’s PAN details. Please try again later.",
                                            })
                                          );
                                          setVerifiedPanSignature("");
                                        } else {
                                          dispatch(
                                            setStatusPopup({
                                              status: "error",
                                              message:
                                                "PAN verification failed. Please check details and try again.",
                                            })
                                          );
                                          setVerifiedPanSignature("");
                                        }
                                      } catch (err: any) {
                                        setVerifiedPanSignature("");
                                        dispatch(
                                          setStatusPopup({
                                            status: "error",
                                            message:
                                              err?.response?.data?.message ||
                                              "PAN verification failed.",
                                          })
                                        );
                                      } finally {
                                        setIsPanVerifying(false);
                                      }
                                    }}
                                    sx={{
                                      height: "30px",
                                      bgcolor: isPanVerifiedForCurrentInputs
                                        ? "#fff"
                                        : "#FEC10D",
                                      color: isPanVerifiedForCurrentInputs
                                        ? "#28C76F"
                                        : "#000",
                                      fontWeight: 700,
                                      borderRadius: "6px",
                                      px: 1,
                                      minWidth: "74px",
                                      textTransform: "none",
                                      fontSize: "12px",
                                      border: "none",
                                      "&:hover": {
                                        bgcolor: isPanVerifiedForCurrentInputs
                                          ? "#fff"
                                          : "#FEC10D",
                                      },
                                    }}
                                    disabled={isPanVerifying}
                                  >
                                    {isPanVerifying
                                      ? "Verifying"
                                      : isPanVerifiedForCurrentInputs
                                        ? (
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px",
                                            }}
                                          >
                                            <CheckCircleIcon
                                              sx={{ fontSize: "14px", color: "#28C76F" }}
                                            />
                                            Verified
                                          </Box>
                                        )
                                        : (
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px",
                                            }}
                                          >
                                            Verify
                                            <img
                                              src={arrowRightVerify}
                                              alt="Verify"
                                              style={{
                                                height: "15px",
                                                objectFit: "contain",
                                                imageRendering:
                                                  "-webkit-optimize-contrast",
                                              }}
                                            />
                                          </Box>
                                        )}
                                  </Button>
                                ),
                              }}
                              error={
                                (form.submitCount > 0 || meta.touched) &&
                                !!meta.error
                              }
                              helperText={
                                (form.submitCount > 0 || meta.touched) &&
                                  meta.error
                                  ? meta.error
                                  : ""
                              }
                            />
                          )}
                        </Field>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Co-applicant's Mobile Number{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <Field name={phoneField}>
                          {({ field: f, meta, form }: any) => (
                            <InputField
                              {...f}
                              fullWidth
                              placeholder="Enter Co-applicant's Mobile Number"
                              variant="outlined"
                              value={f.value}
                              onChange={(e) => {
                                const v = e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 10);
                                setFieldValueAndRevalidate(phoneField, v);
                              }}
                              onBlur={f.onBlur}
                              inputProps={{
                                maxLength: 10,
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                autoComplete: "off",
                              }}
                              onKeyDown={(
                                e: React.KeyboardEvent<HTMLInputElement>
                              ) => {
                                if (
                                  !/[0-9]/.test(e.key) &&
                                  e.key !== "Backspace" &&
                                  e.key !== "Tab" &&
                                  e.key !== "ArrowLeft" &&
                                  e.key !== "ArrowRight" &&
                                  e.key !== "Delete"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              error={
                                (form.submitCount > 0 || meta.touched) &&
                                !!meta.error
                              }
                              helperText={
                                (form.submitCount > 0 || meta.touched) &&
                                  meta.error
                                  ? meta.error
                                  : ""
                              }
                            />
                          )}
                        </Field>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Co-applicant's Email ID{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <Field name={emailField}>
                          {({ field: f, meta, form: formikForm }: any) => (
                            <InputField
                              {...f}
                              fullWidth
                              placeholder="Enter Co-applicant's Email ID"
                              variant="outlined"
                              type="email"
                              autoComplete="new-password"
                              error={
                                (formikForm.submitCount > 0 || meta.touched) &&
                                !!meta.error
                              }
                              helperText={
                                (formikForm.submitCount > 0 || meta.touched) &&
                                  meta.error
                                  ? meta.error
                                  : ""
                              }
                            />
                          )}
                        </Field>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Nationality <span style={{ color: "red" }}>*</span>
                        </label>
                        <AutoCompleteOptions
                          label=""
                          options={nationalityOptions}
                          value={
                            resolveAutocompleteOption(
                              values[nationalityField],
                              nationalityOptions
                            )
                          }
                          isMandatory={false}
                          form={{
                            values,
                            setFieldValue: setFieldValueAndRevalidate,
                            setFieldTouched,
                          }}
                          fieldProps={{ name: nationalityField }}
                          onChange={(opt: any) => {
                            setFieldValueAndRevalidate(nationalityField, opt || "");
                          }}
                          placeholder="Select nationality"
                          error={
                            (submitCount > 0 && !!(errors as any)[nationalityField]) ||
                            (!!(touched as any)[nationalityField] &&
                              !!(errors as any)[nationalityField])
                          }
                          helperText={
                            (submitCount > 0 || (touched as any)[nationalityField]) &&
                            (errors as any)[nationalityField]
                              ? String((errors as any)[nationalityField])
                              : ""
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Marital Status <span style={{ color: "red" }}>*</span>
                        </label>
                        <SelectOptions
                          label=""
                          value={
                            resolveAutocompleteOption(
                              values[maritalStatusField],
                              maritalStatusOptions
                            )?.value ||
                            ((values as any)[maritalStatusField] as
                              | string
                              | number
                              | undefined)
                          }
                          options={maritalStatusOptions}
                          isMandatory={false}
                          placeholder="Select marital status"
                          onChange={(val) =>
                            setFieldValueAndRevalidate(maritalStatusField, val)
                          }
                          error={
                            (submitCount > 0 && !!(errors as any)[maritalStatusField]) ||
                            (!!(touched as any)[maritalStatusField] &&
                              !!(errors as any)[maritalStatusField])
                          }
                          helperText={
                            (submitCount > 0 || (touched as any)[maritalStatusField]) &&
                            (errors as any)[maritalStatusField]
                              ? String((errors as any)[maritalStatusField])
                              : ""
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          Country of Birth <span style={{ color: "red" }}>*</span>
                        </label>
                        <AutoCompleteOptions
                          label=""
                          options={countryOptions}
                          value={
                            resolveAutocompleteOption(
                              values[countryOfBirthField],
                              countryOptions
                            )
                          }
                          isMandatory={false}
                          form={{
                            values,
                            setFieldValue: setFieldValueAndRevalidate,
                            setFieldTouched,
                          }}
                          fieldProps={{ name: countryOfBirthField }}
                          onChange={(opt: any) => {
                            setFieldValueAndRevalidate(countryOfBirthField, opt || "");
                            setFieldValueAndRevalidate(cityOfBirthField, "");
                            setFieldTouched(cityOfBirthField, false, false);
                            lastBirthCityCountryRef.current = "";
                            setCityOfBirthOptions([]);
                            void fetchBirthCities(opt);
                          }}
                          placeholder="Select country of birth"
                          error={
                            (submitCount > 0 && !!(errors as any)[countryOfBirthField]) ||
                            (!!(touched as any)[countryOfBirthField] &&
                              !!(errors as any)[countryOfBirthField])
                          }
                          helperText={
                            (submitCount > 0 || (touched as any)[countryOfBirthField]) &&
                            (errors as any)[countryOfBirthField]
                              ? String((errors as any)[countryOfBirthField])
                              : ""
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <label className="text-lable" style={{ display: "block" }}>
                          City of Birth <span style={{ color: "red" }}>*</span>
                        </label>
                        <AutoCompleteOptions
                          label=""
                          options={cityOfBirthOptions}
                          value={(
                            resolveAutocompleteOption(
                              values[cityOfBirthField],
                              cityOfBirthOptions
                            ) ||
                            (typeof values[cityOfBirthField] === "string" ||
                            typeof values[cityOfBirthField] === "number"
                              ? values[cityOfBirthField]
                              : null)
                          ) as Option | string | number | null}
                          loading={isLoadingBirthCities}
                          disabled={!values[countryOfBirthField]}
                          isMandatory={false}
                          form={{
                            values,
                            setFieldValue: setFieldValueAndRevalidate,
                            setFieldTouched,
                          }}
                          fieldProps={{ name: cityOfBirthField }}
                          onChange={(opt: any) => {
                            setFieldValueAndRevalidate(cityOfBirthField, opt || "");
                          }}
                          placeholder={
                            values[countryOfBirthField]
                              ? "Select city of birth"
                              : "Select country first"
                          }
                          error={
                            (submitCount > 0 && !!(errors as any)[cityOfBirthField]) ||
                            (!!(touched as any)[cityOfBirthField] &&
                              !!(errors as any)[cityOfBirthField])
                          }
                          helperText={
                            (submitCount > 0 || (touched as any)[cityOfBirthField]) &&
                            (errors as any)[cityOfBirthField]
                              ? String((errors as any)[cityOfBirthField])
                              : ""
                          }
                        />
                      </Box>
                    </Grid>
                    </Grid>

                  {nextCoApplicantNumber > 1 && 
                  <Box sx={{ px: 1, pb: 1 }}>
                    <FormControl
                    component="fieldset"
                    error={
                      (submitCount > 0 && !!(errors as any)[payPartyField]) ||
                      ((touched as any)[payPartyField] && !!(errors as any)[payPartyField])
                    }
                    sx={{
                      display: "inline" ,
                      flexDirection: "column", 
                      alignItems: "center",
                    }}
                  >
                    <label className="text-lable" style={{ marginRight: "32px" }}>
                      Do you want to consider income of the co-applicant for loan evaluation? <span style={{ color: "red" }}>*</span>
                    </label>
                    <RadioGroup
                      row
                      value={values[payPartyField]}
                      onChange={(e: any) => {
                        setFieldValueAndRevalidate(payPartyField, e.target.value);
                      }}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label={<span className="text-lable">Yes</span>}
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio />}
                        label={<span className="text-lable">No</span>}
                      />
                    </RadioGroup>
                    <FormHelperText>
                      {(submitCount > 0 || (touched as any)[payPartyField]) && (errors as any)[payPartyField]
                        ? String((errors as any)[payPartyField])
                        : ""}
                    </FormHelperText>
                  </FormControl>
                  </Box>}

                  </>
                  )}

                   {activeTab === 1 && (
                    <AddressDetailsTab
                      addressFieldNames={addressFieldNames}
                      values={values}
                      setFieldValue={setFieldValueAndRevalidate}
                      nextCoApplicantNumber={nextCoApplicantNumber}
                      submitCount={submitCount}
                      errors={errors}
                      touched={touched}
                      handleInvokPinCodeApi={handleInvokPinCodeApi}
                      onZipCodeLoadingChange={setAddressZipCodeLoading}
                    />
                  )}

                  {activeTab === 2 && (
                    <EmployementDetailsTab
                      submitCount={submitCount}
                      touched={touched}
                      errors={errors}
                      employmentFieldNames={employmentFieldNames}
                      values={values}
                      setFieldValue={setFieldValueAndRevalidate}
                      setEmpTypeError={setEmpTypeError}
                      empTypeError={empTypeError}
                      setCompanyCodeData={setCompanyCodeData}
                      nextCoApplicantNumber={nextCoApplicantNumber}
                      handleInvokPinCodeApi={handleInvokPinCodeApi}
                      onZipCodeLoadingChange={setEmploymentZipCodeLoading}
                    />
                  )}
                  {isXsOrSm && <Box>
                    <PrimaryButton
                      type="submit"
                      disabled={disabled || isSubmitting || isLoadingOptions || isPincodeLookupInProgress}
                      sx={{ px: 4, width: "100%" }}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                    </PrimaryButton>
                  </Box>}
                </form>
              );
            }}
          </Formik>
        )}
      </Box>
      {isShowWarning && <ConfirmationModal 
        open={isShowWarning}
        onClose={() => setIsShowWarning(false)}
        onOk={() => {
          setActiveTab(0);
          setIsShowWarning(false);
          onClose();
        }}
        heading="Are you sure about your action?"
        subHeading="Selecting Yes will discard all unsaved co-applicant details and redirect you to the Documents Listing page."
      />}
    </ModalPopup>
  );
};

export default AddNewCoApplicantModal;